import { Injectable } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { CCDL_VALIDATOR_CONSTANTS } from '@cpm-pr1618001/ng-design-lib';
import { Countries } from '@cpm-pr1618003/ng-initializer-lib';
import {
  FPDetails,
  SignInDetails,
  SignUpDetails,
} from '../store/state/landing-page.state';
import {
  ConfirmPasswordMatchValidator,
  PasswordDigitValidator,
  PasswordLowercaseValidator,
  PasswordMinLengthValidator,
  PasswordSpecialCharValidator,
  PasswordUppercaseValidator,
} from './user.validator';

@Injectable({
  providedIn: 'root',
})
export class UserPresenter {
  private formGroup!: FormGroup;
  constructor(private fb: FormBuilder) {}

  initializeFormGroup(): FormGroup {
    this.formGroup = this.fb.group({
      signInForm: this.generateSignInForm(),
      signUpForm: this.generateSignUpForm(),
      fpForm: this.generateFPForm(),
    });
    return this.formGroup;
  }

  getForm(): FormGroup {
    return this.formGroup;
  }

  resetForm(): void {
    this.formGroup?.reset();
  }

  generateSignInForm(): FormGroup {
    return this.fb.group({
      email: this.fb.control(null, [
        Validators.required,
        Validators.pattern(CCDL_VALIDATOR_CONSTANTS.EMAIL_PATTERN),
      ]),
      password: this.fb.control(null, [
        Validators.required,
        Validators.pattern(CCDL_VALIDATOR_CONSTANTS.PASSWORD_PATTERN),
      ]),
      rememberMe: this.fb.control(false),
    });
  }

  generateSignUpForm(): FormGroup {
    return this.fb.group({
      firstName: this.fb.control(null, [
        Validators.required,
        Validators.pattern(CCDL_VALIDATOR_CONSTANTS.NAME_PATTERN),
      ]),
      lastName: this.fb.control(null, [
        Validators.required,
        Validators.pattern(CCDL_VALIDATOR_CONSTANTS.NAME_PATTERN),
      ]),
      email: this.fb.control(null, [
        Validators.required,
        Validators.pattern(CCDL_VALIDATOR_CONSTANTS.EMAIL_PATTERN),
      ]),
      phoneNumber: this.fb.control(null, [
        Validators.required,
        Validators.pattern(CCDL_VALIDATOR_CONSTANTS.PHONE_NUMBER_PATTERN),
      ]),
      password: this.fb.control(null, [
        Validators.required,
        PasswordMinLengthValidator(),
        PasswordUppercaseValidator(),
        PasswordLowercaseValidator(),
        PasswordDigitValidator(),
        PasswordSpecialCharValidator(),
      ]),
      confirmPassword: this.fb.control(null, [
        Validators.required,
        PasswordMinLengthValidator(),
        PasswordUppercaseValidator(),
        PasswordLowercaseValidator(),
        PasswordDigitValidator(),
        PasswordSpecialCharValidator(),
        ConfirmPasswordMatchValidator(),
      ]),
      addressLine1: this.fb.control(null),
      addressLine2: this.fb.control(null),
      country: [{ value: '', disabled: false }, Validators.required],
      state: this.fb.control(null, [Validators.required]),
      zip: this.fb.control(null, [
        Validators.required,
        Validators.pattern(CCDL_VALIDATOR_CONSTANTS.WORLD_ZIP),
      ]),
      rememberMe: this.fb.control(null, [Validators.required]),
    });
  }

  generateFPForm(): FormGroup {
    return this.fb.group({
      email: this.fb.control(null, [
        Validators.required,
        Validators.pattern(CCDL_VALIDATOR_CONSTANTS.EMAIL_PATTERN),
      ]),
      resetOTP: this.fb.control(null, [
        Validators.required,
        Validators.pattern(CCDL_VALIDATOR_CONSTANTS.OTP_MATCH_PATTERN),
      ]),
      newPassword: this.fb.control(null, [
        Validators.required,
        PasswordMinLengthValidator(),
        PasswordUppercaseValidator(),
        PasswordLowercaseValidator(),
        PasswordDigitValidator(),
        PasswordSpecialCharValidator(),
      ]),
      confirmPassword: this.fb.control(null, [
        Validators.required,
        PasswordMinLengthValidator(),
        PasswordUppercaseValidator(),
        PasswordLowercaseValidator(),
        PasswordDigitValidator(),
        PasswordSpecialCharValidator(),
        ConfirmPasswordMatchValidator('newPassword'),
      ]),
    });
  }

  getSignInPayload(): SignInDetails {
    const signInForm: AbstractControl = this.formGroup.get('signInForm');
    return {
      email: signInForm.get('email').value,
      password: signInForm.get('password').value,
      rememberMe: signInForm.get('rememberMe').value,
    };
  }

  getSignUpPayload(chosenCountry: Countries): SignUpDetails {
    const signUpForm: AbstractControl = this.formGroup.get('signUpForm');
    return {
      addressLine1: signUpForm.get('addressLine1').value,
      addressLine2: signUpForm.get('addressLine2').value,
      confirmPassword: signUpForm.get('confirmPassword').value,
      country: signUpForm.get('country').value,
      country_code: chosenCountry.phonePrefixCode,
      emailId: signUpForm.get('email').value,
      firstName: signUpForm.get('firstName').value,
      lastName: signUpForm.get('lastName').value,
      password: signUpForm.get('password').value,
      phoneNumber: signUpForm.get('phoneNumber').value,
      isAgreementAndPrivacyAgreed: signUpForm.get('rememberMe').value,
      state: signUpForm.get('state').value,
      zip: signUpForm.get('zip').value,
    };
  }

  getResetPasswordPayload(): FPDetails {
    const fpForm: AbstractControl = this.formGroup.get('fpForm');
    return {
      emailId: fpForm.get('email').value,
      newPassword: fpForm.get('confirmPassword').value,
      otp: fpForm.get('resetOTP').value,
    };
  }

  isPasswordMatched(password: string, confirmPassword: string): boolean {
    if (!password || !confirmPassword) {
      return false;
    }
    return password === confirmPassword;
  }
}
